import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import DashboardSidebar from "@/pages/dashboard/components/DashboardSidebar";
import MobileBottomNav from "@/pages/dashboard/components/MobileBottomNav";
import SendChart from "@/pages/dashboard/components/SendChart";
import StatsOverview from "./components/StatsOverview";
import SendTypeChart from "./components/SendTypeChart";
import HourlyStats from "./components/HourlyStats";
import CreditTrend from "./components/CreditTrend";
import BalanceFailureBanner from "@/components/feature/BalanceFailureBanner";
import { useBalanceFailureAlert } from "@/hooks/useBalanceFailureAlert";

interface ChartPoint {
  day: string;
  sms: number;
  lms: number;
  mms: number;
}

interface StatsCache {
  credit: number;
  totalSent: number;
  successRate: number;
  apiRequests: number;
  weeklyStats: ChartPoint[];
  monthlyStats: ChartPoint[];
  hourlyData: { hour: string; count: number }[];
  creditTrend: { month: string; used: number; charged: number }[];
  typeCounts: { sms: number; lms: number; mms: number };
}

// 탭 복귀(리로드) 시 로딩 없이 마지막 통계를 즉시 보여주기 위한 캐시
const STATS_CACHE_KEY = "cache:user_stats";

function readStatsCache(): StatsCache | null {
  try {
    const raw = sessionStorage.getItem(STATS_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StatsCache;
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}

function writeStatsCache(next: StatsCache): void {
  try {
    sessionStorage.setItem(STATS_CACHE_KEY, JSON.stringify(next));
  } catch {
    // 캐시 저장 실패(용량 초과 등)는 무시
  }
}

export default function StatsPage() {
  const navigate = useNavigate();
  const { user, loading, session } = useAuth();
  const [collapsed, setCollapsed] = useState(false);

  const cachedStatsRef = useRef<StatsCache | null>(readStatsCache());
  const [statsLoading, setStatsLoading] = useState(cachedStatsRef.current === null);
  const [statsError, setStatsError] = useState<string | null>(null);

  // Overview
  const [credit, setCredit] = useState(cachedStatsRef.current?.credit ?? 0);
  const [totalSent, setTotalSent] = useState(cachedStatsRef.current?.totalSent ?? 0);
  const [successRate, setSuccessRate] = useState(cachedStatsRef.current?.successRate ?? 0);
  const [apiRequests, setApiRequests] = useState(cachedStatsRef.current?.apiRequests ?? 0);

  // Chart data
  const [weeklyStats, setWeeklyStats] = useState<ChartPoint[]>(cachedStatsRef.current?.weeklyStats ?? []);
  const [monthlyStats, setMonthlyStats] = useState<ChartPoint[]>(cachedStatsRef.current?.monthlyStats ?? []);
  const [hourlyData, setHourlyData] = useState<{ hour: string; count: number }[]>(cachedStatsRef.current?.hourlyData ?? []);
  const [creditTrend, setCreditTrend] = useState<{ month: string; used: number; charged: number }[]>(cachedStatsRef.current?.creditTrend ?? []);
  const [typeCounts, setTypeCounts] = useState(cachedStatsRef.current?.typeCounts ?? { sms: 0, lms: 0, mms: 0 });

  // 최근 "잔액 부족" 실패 감지 (통계 화면 경고 배너)
  const balanceAlert = useBalanceFailureAlert(user?.id);
  const refreshBalanceAlert = balanceAlert.refresh;

  useEffect(() => {
    if (!loading && !user) navigate("/login");
  }, [user, loading, navigate]);

  const fetchStats = useCallback(async () => {
    if (!user || !session) return;
    // 캐시된 통계가 있으면 로딩 화면 없이 조용히 갱신
    if (cachedStatsRef.current === null) setStatsLoading(true);
    setStatsError(null);

    const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string;
    const SUPABASE_ANON_KEY = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY as string;

    // 이번 로딩에서 채워질 다음 캐시 (기본값은 기존 캐시에서 이어받음)
    const next: StatsCache = {
      credit: cachedStatsRef.current?.credit ?? 0,
      totalSent: cachedStatsRef.current?.totalSent ?? 0,
      successRate: cachedStatsRef.current?.successRate ?? 0,
      apiRequests: cachedStatsRef.current?.apiRequests ?? 0,
      weeklyStats: cachedStatsRef.current?.weeklyStats ?? [],
      monthlyStats: cachedStatsRef.current?.monthlyStats ?? [],
      hourlyData: cachedStatsRef.current?.hourlyData ?? [],
      creditTrend: cachedStatsRef.current?.creditTrend ?? [],
      typeCounts: cachedStatsRef.current?.typeCounts ?? { sms: 0, lms: 0, mms: 0 },
    };

    try {
      // 1) Profile (credit)
      const profileRes = await fetch(`${SUPABASE_URL}/functions/v1/get-user-profile`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          apikey: SUPABASE_ANON_KEY,
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ user_id: user.id }),
      });
      const profileData = await profileRes.json();
      if (profileData.credit !== undefined) next.credit = Number(profileData.credit);

      // 2) Send stats
      const sendRes = await fetch(`${SUPABASE_URL}/functions/v1/get-user-send-stats`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          apikey: SUPABASE_ANON_KEY,
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ user_id: user.id }),
      });
      const sendData = await sendRes.json();
      if (!sendData.error) {
        const monthHistory = sendData.monthHistory ?? [];
        const sent = monthHistory.filter((h: any) => h.status !== "scheduled");
        const totalSuccess = sent.reduce((s: number, h: any) => s + (h.success_count ?? 0), 0);
        const totalFail = sent.reduce((s: number, h: any) => s + (h.fail_count ?? 0), 0);
        const total = totalSuccess + totalFail;
        next.totalSent = total;
        next.successRate = total > 0 ? Math.round((totalSuccess / total) * 100) : 0;
        next.weeklyStats = sendData.weeklyStats ?? [];
        next.monthlyStats = sendData.monthlyStats ?? [];

        // Type counts from history
        const smsCount = sent.reduce((s: number, h: any) => s + (h.type === "SMS" ? h.total_count ?? 1 : 0), 0);
        const lmsCount = sent.reduce((s: number, h: any) => s + (h.type === "LMS" ? h.total_count ?? 1 : 0), 0);
        const mmsCount = sent.reduce((s: number, h: any) => s + (h.type === "MMS" ? h.total_count ?? 1 : 0), 0);
        next.typeCounts = { sms: smsCount, lms: lmsCount, mms: mmsCount };

        // Hourly distribution
        const hours: Record<number, number> = {};
        sent.forEach((h: any) => {
          const d = new Date(h.created_at || h.sent_at);
          const hr = d.getHours();
          hours[hr] = (hours[hr] || 0) + (h.total_count ?? 1);
        });
        next.hourlyData = Array.from({ length: 12 }, (_, i) => {
          const start = i * 2;
          const end = start + 1;
          const count = (hours[start] || 0) + (hours[end] || 0);
          return { hour: `${String(start).padStart(2, "0")}~${String(end + 1).padStart(2, "0")}`, count };
        });
      }

      // 3) API key requests
      const apiRes = await fetch(`${SUPABASE_URL}/functions/v1/manage-api-keys`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          apikey: SUPABASE_ANON_KEY,
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ action: "list", user_id: user.id }),
      });
      const apiData = await apiRes.json();
      next.apiRequests = (apiData.api_keys || []).reduce((s: number, k: any) => s + (k.request_count ?? 0), 0);

      // 4) Credit transactions for trend
      const creditRes = await fetch(`${SUPABASE_URL}/functions/v1/get-user-credit-transactions`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          apikey: SUPABASE_ANON_KEY,
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ user_id: user.id, page: 1, limit: 100 }),
      });
      const creditData = await creditRes.json();
      const transactions = creditData.transactions ?? [];

      // Group by month
      const monthMap: Record<string, { used: number; charged: number }> = {};
      transactions.forEach((t: any) => {
        const d = new Date(t.created_at);
        const key = `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, "0")}`;
        if (!monthMap[key]) monthMap[key] = { used: 0, charged: 0 };
        if (t.type === "use" || t.type === "deduct") monthMap[key].used += Math.abs(t.amount ?? 0);
        else if (t.type === "charge" || t.type === "bonus" || t.type === "refund") monthMap[key].charged += t.amount ?? 0;
      });
      next.creditTrend = Object.entries(monthMap)
        .sort(([a], [b]) => a.localeCompare(b))
        .slice(-6)
        .map(([month, v]) => ({ month, used: v.used, charged: v.charged }));

      // 반영 + 캐시 갱신
      setCredit(next.credit);
      setTotalSent(next.totalSent);
      setSuccessRate(next.successRate);
      setApiRequests(next.apiRequests);
      setWeeklyStats(next.weeklyStats);
      setMonthlyStats(next.monthlyStats);
      setHourlyData(next.hourlyData);
      setCreditTrend(next.creditTrend);
      setTypeCounts(next.typeCounts);
      writeStatsCache(next);
      cachedStatsRef.current = next;
    } catch (err) {
      console.error("[Stats] fetch error:", err);
      // 캐시가 있으면 화면의 값을 유지하고 조용히 실패
      if (cachedStatsRef.current === null) {
        setStatsError("통계 데이터를 불러오지 못했습니다.");
      }
    } finally {
      cachedStatsRef.current = cachedStatsRef.current ?? {
        credit: 0,
        totalSent: 0,
        successRate: 0,
        apiRequests: 0,
        weeklyStats: [],
        monthlyStats: [],
        hourlyData: [],
        creditTrend: [],
        typeCounts: { sms: 0, lms: 0, mms: 0 },
      };
      setStatsLoading(false);
    }
  }, [user, session]);

  useEffect(() => {
    if (user && session) fetchStats();
  }, [user, session, fetchStats]);

  useEffect(() => {
    if (user && session) refreshBalanceAlert();
  }, [user, session, refreshBalanceAlert]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a18] flex items-center justify-center">
        <i className="ri-loader-4-line animate-spin text-violet-400 text-3xl" />
      </div>
    );
  }
  if (!user) return null;

  const sidebarWidth = collapsed ? "md:ml-16" : "md:ml-60";
  const hasError = statsError !== null;

  return (
    <div className="min-h-screen bg-[#080812] flex">
      <DashboardSidebar collapsed={collapsed} onToggle={() => setCollapsed(!collapsed)} />

      <main className={`flex-1 ${sidebarWidth} transition-all duration-300 min-h-screen pb-20 md:pb-0`}>
        {/* Top bar */}
        <header className="h-14 md:h-16 bg-[#0a0a18]/80 backdrop-blur-md border-b border-white/[0.06] flex items-center justify-between px-4 md:px-6 sticky top-0 z-30">
          <div className="flex items-center gap-2 md:gap-3">
            <button
              onClick={() => navigate("/dashboard")}
              className="w-7 h-7 md:w-8 md:h-8 flex items-center justify-center text-gray-400 hover:text-white cursor-pointer transition-colors"
            >
              <i className="ri-arrow-left-line text-base md:text-lg" />
            </button>
            <div>
              <h1 className="text-white font-semibold text-sm md:text-base">통계</h1>
              <p className="text-gray-500 text-[10px] md:text-xs hidden sm:block">발송 및 사용 현황 분석</p>
            </div>
          </div>
          <div className="flex items-center gap-1.5 md:gap-2">
            <button
              onClick={fetchStats}
              disabled={statsLoading}
              className="w-7 h-7 md:w-8 md:h-8 flex items-center justify-center text-gray-400 hover:text-white cursor-pointer transition-colors rounded-lg hover:bg-white/[0.04]"
              title="새로고침"
            >
              <i className={`ri-refresh-line text-sm md:text-base ${statsLoading ? "animate-spin" : ""}`}></i>
            </button>
            <button
              onClick={() => navigate("/dashboard/deletion-logs")}
              className="hidden sm:flex items-center gap-1 text-gray-500 hover:text-gray-300 text-[10px] md:text-xs font-medium cursor-pointer px-2 py-1 rounded-lg hover:bg-white/[0.04] transition-colors"
              title="삭제 로그"
            >
              <i className="ri-delete-bin-6-line text-xs"></i>
              <span>삭제 로그</span>
            </button>
          </div>
        </header>

        <div className="p-4 md:p-6 space-y-4 md:space-y-6">
          {/* 잔액 부족 실패 경고 */}
          <BalanceFailureBanner state={balanceAlert} />

          {/* 오류 안내 (처음부터 못 불러온 경우에만 노출) */}
          {hasError && (
            <div className="flex items-center justify-between gap-3 px-4 py-3 rounded-xl bg-red-500/10 border border-red-500/20">
              <div className="flex items-center gap-2.5 min-w-0">
                <i className="ri-error-warning-line text-red-400 text-lg shrink-0"></i>
                <p className="text-red-300 text-xs md:text-sm truncate">{statsError}</p>
              </div>
              <button
                onClick={fetchStats}
                className="px-3 py-1.5 rounded-lg bg-red-500/20 text-red-200 text-xs hover:bg-red-500/30 cursor-pointer transition-colors whitespace-nowrap shrink-0"
              >
                다시 시도
              </button>
            </div>
          )}

          {/* Overview cards */}
          <StatsOverview
            totalSent={totalSent}
            successRate={successRate}
            credit={credit}
            apiRequests={apiRequests}
            loading={statsLoading}
          />

          {/* Charts row 1 */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
            <div className="lg:col-span-2">
              <SendChart weeklyData={weeklyStats} monthlyData={monthlyStats} loading={statsLoading} />
            </div>
            <SendTypeChart sms={typeCounts.sms} lms={typeCounts.lms} mms={typeCounts.mms} />
          </div>

          {/* Charts row 2 */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
            <HourlyStats data={hourlyData} />
            <CreditTrend data={creditTrend} />
          </div>
        </div>

        <MobileBottomNav />
      </main>
    </div>
  );
}